import clsx from "clsx";
import { Link, router, usePage } from "@inertiajs/react";
import { LandingNav, LandingFooter } from "@/components/landing/LandingChrome";
import PaperTear from "@/components/landing/PaperTear";
import FaqList, { type FaqItem } from "@/components/landing/FaqList";
import AboutBoba from "@/components/landing/AboutBoba";
import CityHero from "@/components/events/CityHero";
import EventInfoCard from "@/components/events/EventInfoCard";
import SponsorPanel from "@/components/events/SponsorPanel";
import EventPageBlockView from "@/components/events/EventPageBlocks";
import CustomHtmlFrame from "@/components/events/CustomHtmlFrame";
import GuideBody from "@/components/guides/GuideBody";
import Panel from "@/components/ui/Panel";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import Button, { ButtonLink } from "@/components/ui/Button";
import SpeechHeading from "@/components/ui/SpeechHeading";
import SpeechBubble from "@/components/ui/SpeechBubble";
import drink from "@/assets/brand/drink.png";
import checker from "@/assets/brand/checker-tile.webp";
import roomPhoto from "@/assets/photos/hero-13.webp";
import {
  WHAT_YOU_DO,
  WHAT_YOU_DO_BEFORE,
  DEFAULT_FAQ,
} from "@/utils/programCopy";
import type { PublicProps } from "@/types";
import type { SatelliteEvent, EventRsvp } from "@/interfaces/satellite_event";
import type {
  EventPage,
  EventPageBlock,
  FaqBlock,
  ImageBlock,
  SponsorsBlock,
  TextBlock,
} from "@/interfaces/event_page";

// The city page is a designed layout, not a stack of sections: each block type
// was written for a particular slot in the Figma, so it goes there. Anything
// with no slot of its own runs down the middle in the order it was written.
//
// Second FAQ and sponsor sections fall through to the middle rather than being
// dropped - an organizer who added one meant it to appear somewhere.
function slot(blocks: EventPageBlock[]) {
  const gallery: { url: string; caption: string | null }[] = [];
  let galleryHeading: string | null = null;
  const about: TextBlock[] = [];
  const photos: ImageBlock[] = [];
  const middle: EventPageBlock[] = [];
  let faq: FaqBlock | null = null;
  let sponsors: SponsorsBlock | null = null;

  for (const block of blocks) {
    switch (block.type) {
      case "gallery":
        gallery.push(...block.items);
        galleryHeading ??= block.heading;
        break;
      case "text":
        about.push(block);
        break;
      case "image":
        photos.push(block);
        break;
      case "faq":
        if (faq) middle.push(block);
        else faq = block;
        break;
      case "sponsors":
        if (sponsors) middle.push(block);
        else sponsors = block;
        break;
      default:
        middle.push(block);
    }
  }

  // The first photo is the venue shot beside the event details; any others
  // join the middle of the page.
  const [venue, ...rest] = photos;
  return {
    gallery,
    galleryHeading,
    about,
    venue,
    faq,
    sponsors,
    middle: [...rest, ...middle],
  };
}

export type PageSlot =
  | "gallery"
  | "about"
  | "venue"
  | "middle"
  | "sponsors"
  | "faq";

// Which part of the page a given section ends up in. Exported so the editor can
// point at the same place this component actually renders it, instead of
// keeping a second copy of the rules that would drift out of step.
export function slotFor(blocks: EventPageBlock[], index: number): PageSlot {
  const block = blocks[index];
  if (!block) return "middle";

  // Only the first photo, FAQ and sponsor section get their own slot; the rest
  // fall through to the middle, exactly as slot() sorts them.
  const isFirstOfType =
    blocks.findIndex((b) => b.type === block.type) === index;

  switch (block.type) {
    case "gallery":
      return "gallery";
    case "text":
      return "about";
    case "image":
      return isFirstOfType ? "venue" : "middle";
    case "faq":
      return isFirstOfType ? "faq" : "middle";
    case "sponsors":
      return isFirstOfType ? "sponsors" : "middle";
    default:
      return "middle";
  }
}

export default function CityPage({
  event,
  page,
  myRsvp,
  isOrganizer,
  ageRange,
  preview = false,
  highlight = null,
}: {
  event: SatelliteEvent;
  // The saved page on the public route, the unsaved draft in the editor. Passed
  // in rather than read off `event` so the editor can render what the organizer
  // is typing through the same component an attendee gets.
  page: EventPage | null;
  myRsvp: EventRsvp | null;
  isOrganizer: boolean;
  ageRange: { min: number; max: number };
  preview?: boolean;
  // The editor sets this while a section is focused, so "where does this end
  // up?" is answered by the page lighting up rather than by a description.
  highlight?: PageSlot | null;
}) {
  const { props } = usePage<PublicProps>();
  const program = props.program;
  // Approved events are shared before they take sign-ups. Cancelling stays
  // available either way, so nobody who already RSVP'd is stuck holding a spot.
  const rsvpsOpen = program.rsvps_open;
  const going = myRsvp != null && myRsvp.aasm_state !== "cancelled";
  const signupHref = `/events/${event.slug ?? event.id}/rsvp`;
  const canSignUp =
    !going && !isOrganizer && !event.past && rsvpsOpen && !event.full;

  const slots = slot(page?.blocks ?? []);
  const lit = (name: PageSlot) =>
    clsx(highlight === name && "ring-sunset-orange ring-4 ring-offset-2");
  const customHtml = page?.custom_html;

  const faqItems: FaqItem[] = slots.faq
    ? slots.faq.items.map((item) => ({
        q: item.question,
        a: item.answer,
        html: item.answer_html,
      }))
    : DEFAULT_FAQ;

  // In the editor this is a picture of the page, not the page. Links and the
  // hero's email form are stopped in the capture phase, which is early enough
  // that Inertia's own click handler never runs and the organizer never gets
  // navigated out of the editor mid-sentence. Plain buttons are left alone, so
  // the FAQ still opens and closes.
  const inert = preview
    ? {
        onClickCapture: (e: React.MouseEvent) => {
          if ((e.target as HTMLElement).closest("a")) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        onSubmitCapture: (e: React.FormEvent) => {
          e.preventDefault();
          e.stopPropagation();
        },
      }
    : {};

  return (
    <div {...inert}>
      <div id="top" className="text-ink min-h-screen bg-white antialiased">
        <LandingNav signedIn={props.user != null} signUpHref={signupHref} />

        {isOrganizer && !preview && (
          <div className="border-line bg-goldenrod flex flex-wrap items-center justify-center gap-3 border-b-2 px-4 py-2">
            <span className="font-display text-caption text-ink font-bold">
              {event.page_published
                ? "This is your city page, live."
                : "Your page isn't published yet, so only you can see it."}
            </span>
            <ButtonLink
              href={`/organize/${event.id}/page`}
              variant="secondary"
              size="sm"
            >
              Edit this page
            </ButtonLink>
            <ButtonLink
              href={`/organize/${event.id}`}
              variant="secondary"
              size="sm"
            >
              Manage event
            </ButtonLink>
          </div>
        )}

        <CityHero
          event={event}
          highlightPhotos={highlight === "gallery"}
          photos={slots.gallery}
          photosTitle={slots.galleryHeading}
          signupHref={signupHref}
          rsvpsOpen={rsvpsOpen}
          going={going}
          ageRange={ageRange}
        />

        {going && (
          <section className="border-line border-b-2 bg-white px-4 py-6 sm:px-6">
            <div className="mx-auto w-full max-w-6xl">
              <Panel title="you-re-going.txt">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <p className="text-body text-ink">
                      You're on the list for {event.name}. See you there!
                    </p>
                    {myRsvp?.confirmed === false && (
                      <p className="text-caption text-ink-muted">
                        We've emailed you a link to confirm your spot - click it
                        so your organizer knows you're really coming.
                      </p>
                    )}
                    {/* The next thing to do, said at the moment they've just
                        done the last one. Somewhere that collects sites before
                        the day, "see you there" on its own is what produces a
                        room full of people who built nothing. */}
                    {event.prefunding_deadline && event.submissions_open && (
                      <p className="text-caption text-ink-muted">
                        Next: build a website and hand it in before the event
                        starts
                        {event.prefunding_deadline_label
                          ? ` on ${event.prefunding_deadline_label}`
                          : ""}
                        .{" "}
                        <Link
                          href={`/events/${event.slug ?? event.id}/submit`}
                          className="text-link font-bold underline"
                        >
                          Hand yours in
                        </Link>
                        .
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      router.delete(`/events/${event.slug ?? event.id}/rsvp`)
                    }
                  >
                    Cancel RSVP
                  </Button>
                </div>
              </Panel>
            </div>
          </section>
        )}

        {/* Handing in what you built. Shown to everyone, not just the people
            holding an RSVP: the gallery is the payoff and it reads as an
            invitation. The form behind it still checks the roster. */}
        {(event.submissions_open || (event.submission_count ?? 0) > 0) && (
          <section className="border-line border-b-2 bg-white px-4 py-6 sm:px-6">
            <div className="mx-auto w-full max-w-6xl">
              <Panel
                title={
                  event.prefunding_deadline
                    ? "build-one-first.txt"
                    : "hand-it-in.txt"
                }
              >
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {/* Two different asks, not two wordings of one.
                        Somewhere that builds on the day invites you to hand in
                        what you made; somewhere that collects sites beforehand
                        has to tell you to go and make one, because turning up
                        empty-handed is the failure mode. */}
                    {event.prefunding_deadline ? (
                      <p className="font-display text-subheading text-ink font-bold">
                        {event.submissions_open
                          ? "Build a website before you come."
                          : "Here's everything this city built."}
                      </p>
                    ) : (
                      <p className="text-body text-ink">
                        {event.submissions_open
                          ? "Built something? Hand it in and it goes up in the gallery."
                          : "Here's everything that got built at this one."}
                      </p>
                    )}
                    {event.submissions_open &&
                      event.prefunding_deadline &&
                      event.prefunding_deadline_label && (
                        <p className="text-body text-ink-muted">
                          Plain HTML and CSS, made at home in your own time. Get
                          it in{" "}
                          <span className="text-ink font-bold">
                            before the event starts
                          </span>{" "}
                          on {event.prefunding_deadline_label} - that's what
                          gets this Bash funded, so it's the one thing worth
                          doing early. Someone looks at every site before it
                          goes on the wall.
                        </p>
                      )}
                    {event.submissions_open &&
                      !event.prefunding_deadline &&
                      event.submissions_close_label && (
                        <p className="text-caption text-ink-muted">
                          Open until {event.submissions_close_label} - you can
                          keep changing it until then.
                        </p>
                      )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {(event.submission_count ?? 0) > 0 && (
                      <ButtonLink
                        href={`/events/${event.slug ?? event.id}/submissions`}
                        variant="secondary"
                      >
                        See the gallery
                      </ButtonLink>
                    )}
                    {event.submissions_open && (
                      <ButtonLink
                        href={`/events/${event.slug ?? event.id}/submit`}
                      >
                        {event.prefunding_deadline
                          ? "Hand in your site now"
                          : "Hand in your site"}
                      </ButtonLink>
                    )}
                  </div>
                </div>
              </Panel>
            </div>
          </section>
        )}

        {/* What is it. The organizer's own words when they wrote some, the
            program's when they didn't - never both, or the page says it twice. */}
        <section id="what" className="bg-surface px-4 py-16 sm:px-6">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
            <div className="flex items-end gap-4">
              <SpeechHeading title={`What is a ${program.name}?`} level="h2" />
              <img
                src={drink}
                alt=""
                aria-hidden
                className="h-20 w-auto shrink-0 sm:h-24"
              />
            </div>

            <div className="flex flex-col items-start gap-6 md:flex-row">
              {/* Deliberately the stock shot and not the organizer's photo:
                  theirs is the venue, and the venue has its own slot further
                  down. Using it here printed the same picture twice. */}
              <img
                src={roomPhoto}
                alt="Teenagers building things around a long table at a Hack Club event"
                className="border-line shadow-card h-64 w-full shrink-0 border-2 object-cover md:h-80 md:w-72"
              />

              <SpeechBubble
                className={clsx("flex-1", lit("about"))}
                data-slot="about"
                bodyClassName="text-body text-ink-muted flex flex-col gap-4"
              >
                {slots.about.length > 0 ? (
                  slots.about.map((block, i) =>
                    // `html` is the sanitized render; until it arrives the raw
                    // text stands in, so a live preview never blanks out
                    // mid-sentence. Never render `body` as markup.
                    block.html ? (
                      <GuideBody key={i} html={block.html} />
                    ) : (
                      <p key={i} className="whitespace-pre-line">
                        {block.body}
                      </p>
                    ),
                  )
                ) : event.description && !event.page_published ? (
                  <p className="whitespace-pre-line">{event.description}</p>
                ) : (
                  <AboutBoba />
                )}
              </SpeechBubble>
            </div>
          </div>
        </section>

        {/* What you do. A full-bleed checkerboard band torn out of the white
            page above and below it, exactly as the Figma stages it. */}
        <section
          className="bg-white bg-repeat"
          style={{ backgroundImage: `url(${checker})`, backgroundSize: "96px" }}
        >
          <PaperTear />

          <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-4 py-10 sm:px-6">
            <SpeechHeading
              title={`What do you do at a ${program.name}?`}
              level="h2"
            />

            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-3">
              {(event.prefunding_deadline
                ? WHAT_YOU_DO_BEFORE
                : WHAT_YOU_DO
              ).map((step, i) => (
                <Card
                  key={step.title}
                  // The Figma lifts the middle card out of line with the other
                  // two. Only once there are three columns to break.
                  className={i === 1 ? "sm:-translate-y-6" : undefined}
                  media={
                    <img
                      src={step.art}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="aspect-square w-full object-cover"
                    />
                  }
                >
                  <h3 className="font-display text-title text-ink text-center font-bold">
                    {step.title}
                  </h3>
                  <p className="text-caption text-ink-muted mt-2 text-center">
                    {step.body}
                  </p>
                </Card>
              ))}
            </div>

            {canSignUp && (
              <Link
                href={signupHref}
                className="font-display text-heading text-sunset-orange font-bold italic underline decoration-2 underline-offset-4"
              >
                sign up for {event.city ?? event.name} &gt;&gt;&gt;
              </Link>
            )}
          </div>

          <PaperTear flip />
        </section>

        {/* The facts the platform is responsible for. Server-rendered and
            outside the organizer's blocks on purpose: nothing an organizer
            writes can restate the capacity or move the address disclosure. */}
        <section id="info" className="bg-goldenrod/25 px-4 py-16 sm:px-6">
          <div className="mx-auto grid w-full max-w-6xl items-start gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="flex flex-col gap-4">
              <EventInfoCard
                event={event}
                signupHref={signupHref}
                showSignup={canSignUp}
              />

              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">{event.rsvp_count ?? 0} going</Badge>
                {event.full ? (
                  <Badge tone="warning">Full</Badge>
                ) : (
                  event.spots_left != null && (
                    <Badge tone="info">{event.spots_left} spots left</Badge>
                  )
                )}
              </div>

              {event.organizer_name && (
                <p className="text-caption text-ink-soft">
                  Run by{" "}
                  <span className="font-bold">{event.organizer_name}</span>, a
                  teenager in {event.city ?? "your city"}.
                </p>
              )}
            </div>

            {slots.venue && (
              <figure
                data-slot="venue"
                className={clsx(
                  "border-line shadow-card border-2 bg-white p-3",
                  lit("venue"),
                )}
              >
                <img
                  src={slots.venue.url}
                  alt={slots.venue.caption ?? ""}
                  loading="lazy"
                  className="border-line w-full border object-cover"
                />
                <figcaption className="text-caption text-ink-soft pt-2 italic">
                  {slots.venue.caption ?? "the venue!"}
                </figcaption>
              </figure>
            )}
          </div>
        </section>

        {(slots.middle.length > 0 || customHtml) && (
          <section className="bg-surface px-4 py-12 sm:px-6">
            <div
              data-slot="middle"
              className={clsx(
                "mx-auto flex w-full max-w-4xl flex-col gap-5",
                lit("middle"),
              )}
            >
              {slots.middle.map((block, i) => (
                <EventPageBlockView key={i} block={block} />
              ))}

              {customHtml && (
                <CustomHtmlFrame
                  html={customHtml}
                  height={page?.custom_html_height ?? 600}
                />
              )}
            </div>
          </section>
        )}

        {slots.sponsors && (
          <section className="bg-band-sky px-4 py-12 sm:px-6">
            <div
              data-slot="sponsors"
              className={clsx("mx-auto w-full max-w-5xl", lit("sponsors"))}
            >
              <SponsorPanel block={slots.sponsors} />
            </div>
          </section>
        )}

        {/* FAQ. The Figma drops the whole band onto sidecar grid paper. */}
        <section id="faq" className="bg-band-faq relative">
          <PaperTear />

          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(97,51,1,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(97,51,1,.06) 1px, transparent 1px)",
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-12 sm:px-6">
            <div className="flex flex-wrap items-baseline gap-4 border-b-2 border-current/20 pb-2">
              <h2 className="font-display text-display text-ink-muted font-bold">
                FAQ
              </h2>
              <span className="text-subheading text-ink-muted italic">
                {slots.faq?.heading ?? "frequently asked questions"}
              </span>
            </div>

            <Panel
              title="faq.csv"
              tone="alt"
              data-slot="faq"
              className={lit("faq")}
            >
              <FaqList items={faqItems} />

              <p className="text-caption text-ink-soft mt-6 text-center">
                Still stuck? Ask us in{" "}
                {program.support_channel_name ? (
                  <a
                    className="font-bold underline"
                    href={program.support_channel_url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    #{program.support_channel_name}
                  </a>
                ) : (
                  "our Slack"
                )}
                , or look at{" "}
                <Link className="font-bold underline" href="/events">
                  the other cities
                </Link>
                .
              </p>
            </Panel>
          </div>
        </section>

        <LandingFooter />
      </div>
    </div>
  );
}
